import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate, useBlocker } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import {
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  PencilSquareIcon,
} from '@heroicons/react/20/solid';
import CustomerPicker from '../components/CustomerPicker';
import FreeformQuoteLineEditor from '../components/FreeformQuoteLineEditor';
import PillSelect from '../components/PillSelect';
import { Input, Textarea, FormGrid, Modal, ModalFooter, ModalButton, Select } from '../components/ui';
import { PAYMENT_TERMS_OPTIONS } from '../utils/poUtils';
import { generateFreeformQuoteNumber, generateFreeformQuotePDF } from '../utils/freeformQuoteUtils';
import { useLocalDraft, readLocalDraft, clearLocalDraft } from '../hooks/useLocalDraft';

const STATUS_OPTIONS = [
  { value: 'draft',     label: 'Draft' },
  { value: 'sent',      label: 'Sent' },
  { value: 'accepted',  label: 'Accepted' },
  { value: 'declined',  label: 'Declined' },
  { value: 'expired',   label: 'Expired' },
  { value: 'cancelled', label: 'Cancelled' },
];

const STATUS_PILL = {
  draft:     'bg-slate-100 text-slate-700 border-slate-300',
  sent:      'bg-sky-50 text-sky-700 border-sky-300',
  accepted:  'bg-emerald-50 text-emerald-700 border-emerald-300',
  declined:  'bg-rose-50 text-rose-700 border-rose-300',
  expired:   'bg-amber-50 text-amber-700 border-amber-300',
  cancelled: 'bg-slate-200 text-slate-700 border-slate-300',
};

const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;

const blankLine = () => ({
  _key: `new-${Math.random().toString(36).slice(2, 9)}`,
  id: null,
  line_number: '',
  description: '',
  quantity: '',
  uom: 'ea',
  unit_price: '',
  notes: '',
});

export default function FreeformQuote() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [quote, setQuote] = useState(null);
  const [lines, setLines] = useState([]);
  const [deletedLineIds, setDeletedLineIds] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [isEditMode, setIsEditMode] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [restoredFromDraft, setRestoredFromDraft] = useState(false);
  const [draftChecked, setDraftChecked] = useState(false);
  const newLineKeyRef = useRef(0);

  const draftKey = id ? `freeform-quote-draft:${id}` : null;

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => { document.title = 'Freeform Quote'; }, []);

  useEffect(() => {
    const handler = (e) => { if (isDirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const fetchQuote = async (silent = false) => {
    if (!id) return;
    if (!silent) setLoading(true);
    try {
      const [quoteRes, linesRes] = await Promise.all([
        supabase
          .from('freeform_quotes')
          .select(`
            *,
            customers(id, name, nickname,
              billing_address, billing_city, billing_state, billing_zip,
              billing_contact_name, billing_contact_email, billing_contact_phone,
              shipping_address, shipping_city, shipping_state, shipping_zip,
              shipping_contact_name, shipping_contact_email, shipping_contact_phone)
          `)
          .eq('id', id)
          .single(),
        supabase
          .from('freeform_quote_lines')
          .select('*')
          .eq('freeform_quote_id', id)
          .order('line_number'),
      ]);
      if (quoteRes.error) throw quoteRes.error;
      if (linesRes.error) throw linesRes.error;
      setQuote(quoteRes.data);
      setCustomer(quoteRes.data.customers || null);
      setLines((linesRes.data || []).map((l) => ({
        ...l,
        _key: `db-${l.id}`,
        quantity: l.quantity ?? '',
        unit_price: l.unit_price ?? '',
        line_number: l.line_number ?? '',
      })));
      setDeletedLineIds([]);
      setIsDirty(false);
      setIsEditMode(quoteRes.data.status === 'draft');
    } catch (err) {
      console.error('Failed to load quote:', err);
      toast.error('Failed to load quote.');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const refreshQuote = () => fetchQuote(true);

  useEffect(() => {
    if (user) fetchQuote();
    // Depend on user?.id (stable) — `user` object reference changes on Supabase
    // auth token refresh, which would otherwise wipe in-progress edits on tab focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.id]);

  // Restore unsaved local draft (if any) after initial fetch
  useEffect(() => {
    if (!quote || draftChecked || !draftKey) return;
    const draft = readLocalDraft(draftKey);
    if (draft?.data?.quote && draft.data.quote.id === id) {
      setQuote(draft.data.quote);
      setLines(draft.data.lines || []);
      setDeletedLineIds(draft.data.deletedLineIds || []);
      setIsDirty(true);
      setIsEditMode(true);
      setRestoredFromDraft(true);
    }
    setDraftChecked(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.id, draftKey, draftChecked]);

  // Auto-save in-progress edits to localStorage
  const draftSnapshot = useMemo(
    () => ({ quote, lines, deletedLineIds }),
    [quote, lines, deletedLineIds]
  );
  useLocalDraft({ key: draftKey, isDirty, snapshot: draftSnapshot });

  const dismissDraft = async () => {
    clearLocalDraft(draftKey);
    setRestoredFromDraft(false);
    await fetchQuote();
  };

  const totals = useMemo(() => {
    const subtotal = lines.reduce(
      (s, l) => s + Number(l.quantity || 0) * Number(l.unit_price || 0),
      0
    );
    return { subtotal, total: subtotal };
  }, [lines]);

  const setField = (field) => (e) => {
    const val = e?.target?.value ?? e;
    setQuote((q) => ({ ...q, [field]: val }));
    setIsDirty(true);
  };

  const setNumField = (field) => (e) => {
    setQuote((q) => ({ ...q, [field]: e.target.value }));
    setIsDirty(true);
  };

  const handleCustomerChange = (customerId, row) => {
    setQuote((q) => ({ ...q, customer_id: customerId }));
    setCustomer(row);
    setIsDirty(true);
  };

  const addLine = () => {
    const nextLineNum = (lines.reduce((max, l) => Math.max(max, parseInt(l.line_number) || 0), 0)) + 1;
    newLineKeyRef.current += 1;
    setLines((ls) => [
      ...ls,
      { ...blankLine(), _key: `new-${newLineKeyRef.current}`, line_number: String(nextLineNum), uom: 'ea' },
    ]);
    setIsDirty(true);
  };

  const updateLine = (idx, patch) => {
    setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
    setIsDirty(true);
  };

  const removeLine = (idx) => {
    setLines((ls) => {
      const line = ls[idx];
      if (line?.id) setDeletedLineIds((ids) => [...ids, line.id]);
      return ls.filter((_, i) => i !== idx);
    });
    setIsDirty(true);
  };

  const buildLinePayload = (l) => ({
    freeform_quote_id: id,
    line_number: parseInt(l.line_number) || 0,
    description: l.description?.trim() || null,
    quantity: Number(l.quantity || 0),
    uom: l.uom || 'ea',
    unit_price: Number(l.unit_price || 0),
    extended_price: Number(l.quantity || 0) * Number(l.unit_price || 0),
    notes: l.notes?.trim() || null,
  });

  const num = (v) => (v === '' || v == null ? 0 : Number(v));

  const saveCore = async () => {
    if (!quote.quote_number?.trim()) {
      toast.error('Quote number is required.');
      return false;
    }
    if (quote.status === 'sent') {
      if (!quote.customer_id) { toast.error('Pick a customer before sending.'); return false; }
      if (lines.length === 0) { toast.error('Add at least one line before sending.'); return false; }
    }

    const payload = {
      quote_number: quote.quote_number.trim(),
      status: quote.status || 'draft',
      customer_id: quote.customer_id || null,
      contact: quote.contact?.trim() || null,
      description: quote.description?.trim() || null,
      quote_date: quote.quote_date || new Date().toISOString().slice(0, 10),
      quote_valid_days: parseInt(quote.quote_valid_days, 10) || 30,
      quoted_by: quote.quoted_by?.trim() || null,
      customer_rfq_po: quote.customer_rfq_po?.trim() || null,
      payment_terms: quote.payment_terms || null,
      shipping_fob: quote.shipping_fob?.trim() || null,
      lead_time: quote.lead_time?.trim() || null,
      customer_notes: quote.customer_notes?.trim() || null,
      internal_notes: quote.internal_notes?.trim() || null,
      subtotal: totals.subtotal,
      total: totals.total,
    };
    if (quote.status === 'sent' && !quote.sent_at) payload.sent_at = new Date().toISOString();
    if (quote.status === 'accepted' && !quote.accepted_at) payload.accepted_at = new Date().toISOString();
    if (quote.status === 'declined' && !quote.declined_at) payload.declined_at = new Date().toISOString();

    const { error: qErr } = await supabase.from('freeform_quotes').update(payload).eq('id', id);
    if (qErr) throw qErr;

    if (deletedLineIds.length > 0) {
      const { error: delErr } = await supabase
        .from('freeform_quote_lines')
        .delete()
        .in('id', deletedLineIds);
      if (delErr) throw delErr;
    }

    const existing = lines.filter((l) => l.id);
    for (const l of existing) {
      const { error: upErr } = await supabase
        .from('freeform_quote_lines')
        .update(buildLinePayload(l))
        .eq('id', l.id);
      if (upErr) throw upErr;
    }

    const newOnes = lines.filter((l) => !l.id);
    if (newOnes.length > 0) {
      const { error: insErr } = await supabase
        .from('freeform_quote_lines')
        .insert(newOnes.map(buildLinePayload));
      if (insErr) throw insErr;
    }

    return true;
  };

  const handleSave = async (exitOnSuccess = true) => {
    if (saving) return false;
    setSaving(true);
    try {
      const ok = await saveCore();
      if (!ok) return false;
      toast.success('Quote saved.');
      setIsDirty(false);
      clearLocalDraft(draftKey);
      setRestoredFromDraft(false);
      await refreshQuote();
      if (exitOnSuccess) setIsEditMode(false);
      return true;
    } catch (err) {
      console.error('Save failed:', err);
      toast.error(err.message || 'Failed to save quote.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const persistStatusChange = async (newStatus) => {
    try {
      const update = { status: newStatus };
      if (newStatus === 'sent' && !quote.sent_at) update.sent_at = new Date().toISOString();
      if (newStatus === 'accepted' && !quote.accepted_at) update.accepted_at = new Date().toISOString();
      if (newStatus === 'declined') update.declined_at = new Date().toISOString();
      const { error } = await supabase.from('freeform_quotes').update(update).eq('id', id);
      if (error) throw error;
      toast.success('Status updated.');
      await refreshQuote();
    } catch (err) {
      console.error('Status update failed:', err);
      toast.error(err.message || 'Failed to update status.');
    }
  };

  const handleQuickStatusChange = async (newStatus) => {
    if (!quote || newStatus === quote.status) return;
    if (newStatus === 'sent') {
      if (!quote.customer_id) { toast.error('Pick a customer before sending.'); return; }
      if (lines.length === 0) { toast.error('Add at least one line before sending.'); return; }
    }
    await persistStatusChange(newStatus);
  };

  const handleDuplicate = async () => {
    try {
      const newNumber = await generateFreeformQuoteNumber();
      const { data: newQuote, error: qErr } = await supabase
        .from('freeform_quotes')
        .insert({
          ...quote,
          id: undefined,
          quote_number: newNumber,
          status: 'draft',
          quote_date: new Date().toISOString().slice(0, 10),
          sent_at: null,
          accepted_at: null,
          declined_at: null,
          created_by: user?.id || null,
          created_at: undefined,
          updated_at: undefined,
          customers: undefined,
        })
        .select('id')
        .single();
      if (qErr) throw qErr;

      // Copy lines into the new quote
      if (lines.length > 0) {
        const lineCopies = lines.map((l) => ({
          freeform_quote_id: newQuote.id,
          line_number: parseInt(l.line_number) || 0,
          description: l.description,
          quantity: num(l.quantity),
          uom: l.uom || 'ea',
          unit_price: num(l.unit_price),
          extended_price: num(l.quantity) * num(l.unit_price),
          notes: l.notes,
        }));
        const { error: lErr } = await supabase.from('freeform_quote_lines').insert(lineCopies);
        if (lErr) throw lErr;
      }

      toast.success(`Duplicated as ${newNumber}.`);
      navigate(`/freeform-quotes/${newQuote.id}`);
    } catch (err) {
      console.error('Duplicate failed:', err);
      toast.error(err.message || 'Failed to duplicate.');
    }
  };

  const confirmDelete = async () => {
    try {
      const { error } = await supabase.from('freeform_quotes').delete().eq('id', id);
      if (error) throw error;
      clearLocalDraft(draftKey);
      setDeleteOpen(false);
      setIsDirty(false);
      toast.success('Quote deleted.');
      navigate('/freeform-quotes');
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error(err.message || 'Failed to delete quote.');
    }
  };

  const handleDownloadPDF = async () => {
    try {
      await generateFreeformQuotePDF({ quote, lines, customer });
    } catch (err) {
      console.error('PDF failed:', err);
      toast.error('Failed to generate PDF.');
    }
  };

  const enterEditMode = () => setIsEditMode(true);
  const handleCancelEdit = () => {
    if (isDirty) setDiscardConfirmOpen(true);
    else setIsEditMode(false);
  };
  const confirmDiscard = async () => {
    setDiscardConfirmOpen(false);
    setIsDirty(false);
    clearLocalDraft(draftKey);
    setRestoredFromDraft(false);
    await fetchQuote();
    setIsEditMode(false);
  };

  const blockerStay = () => blocker.reset?.();
  const blockerDiscard = () => {
    setIsDirty(false);
    clearLocalDraft(draftKey);
    setRestoredFromDraft(false);
    blocker.proceed?.();
  };
  const blockerSaveAndLeave = async () => { const ok = await handleSave(false); if (ok) blocker.proceed?.(); };

  if (loading) return <div className="p-4 text-sm text-slate-500">Loading…</div>;
  if (!quote) {
    return (
      <div className="p-4">
        <Link to="/freeform-quotes" className="inline-flex items-center gap-1 text-sm text-sky-700 hover:underline">
          <ArrowLeftIcon className="w-4 h-4" /> Back to Quotes
        </Link>
        <p className="mt-4 text-sm text-slate-500">Quote not found.</p>
      </div>
    );
  }

  const readOnly = !isEditMode;

  const billTo = customer ? {
    name: customer.nickname || customer.name,
    contact: customer.billing_contact_name,
    address: customer.billing_address,
    city: customer.billing_city, state: customer.billing_state, zip: customer.billing_zip,
    email: customer.billing_contact_email,
    phone: customer.billing_contact_phone,
  } : null;
  const shipTo = customer ? {
    name: customer.nickname || customer.name,
    contact: customer.shipping_contact_name,
    address: customer.shipping_address,
    city: customer.shipping_city, state: customer.shipping_state, zip: customer.shipping_zip,
    email: customer.shipping_contact_email,
    phone: customer.shipping_contact_phone,
  } : null;

  return (
    <div className="p-4 space-y-4 max-w-6xl">
      <Link to="/freeform-quotes" className="inline-flex items-center gap-1 text-sm text-sky-700 hover:underline">
        <ArrowLeftIcon className="w-4 h-4" /> Back to Quotes
      </Link>

      {restoredFromDraft && (
        <div className="px-3 py-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm flex items-center justify-between">
          <span>Restored unsaved changes from a previous session.</span>
          <button onClick={dismissDraft} className="text-rose-700 hover:underline ml-3">
            Discard local changes
          </button>
        </div>
      )}

      {/* Header card */}
      <div className="bg-white border border-slate-200 rounded-sm px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-lg font-semibold text-slate-800">{quote.quote_number}</h1>
          <PillSelect
            value={quote.status || 'draft'}
            onChange={(e) => readOnly ? handleQuickStatusChange(e.target.value) : setField('status')(e)}
            options={STATUS_OPTIONS}
            pillClass={STATUS_PILL[quote.status] || 'bg-slate-100 text-slate-700 border-slate-300'}
          />
          {isDirty && <span className="text-xs text-amber-700 italic">· unsaved changes</span>}
        </div>
        <div className="flex items-center gap-2">
          {readOnly ? (
            <button onClick={enterEditMode} className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-slate-700 text-white rounded-sm hover:bg-slate-800">
              <PencilSquareIcon className="w-4 h-4" /> Edit
            </button>
          ) : (
            <>
              <button onClick={() => handleSave(true)} disabled={saving} className="px-3 py-1.5 text-sm font-medium bg-slate-700 text-white rounded-sm hover:bg-slate-800 disabled:bg-slate-400">
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button onClick={handleCancelEdit} disabled={saving} className="px-3 py-1.5 text-sm font-medium bg-white text-slate-700 border border-slate-200 rounded-sm hover:bg-slate-50 disabled:opacity-50">
                Cancel
              </button>
            </>
          )}
          {readOnly && (
            <button onClick={handleDownloadPDF} className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-white text-slate-700 border border-slate-200 rounded-sm hover:bg-slate-50">
              <ArrowDownTrayIcon className="w-4 h-4" /> PDF
            </button>
          )}
          <button onClick={handleDuplicate} className="px-3 py-1.5 text-sm font-medium bg-white text-slate-700 border border-slate-200 rounded-sm hover:bg-slate-50">
            Duplicate
          </button>
          {quote.status === 'draft' && (
            <button onClick={() => setDeleteOpen(true)} className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-white text-slate-500 border border-slate-200 rounded-sm hover:bg-rose-50 hover:text-rose-700" aria-label="Delete quote">
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Header form */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-4">
        <FormGrid columns={4}>
          <Select label="Payment Terms" value={quote.payment_terms || ''} onChange={setField('payment_terms')} options={PAYMENT_TERMS_OPTIONS} placeholder="— Select —" disabled={readOnly} />
          <Input label="Shipping / FOB" value={quote.shipping_fob || ''} onChange={setField('shipping_fob')} disabled={readOnly} />
          <Input label="Lead Time" value={quote.lead_time || ''} onChange={setField('lead_time')} disabled={readOnly} />
          <Input label="Customer RFQ/PO" value={quote.customer_rfq_po || ''} onChange={setField('customer_rfq_po')} disabled={readOnly} />
        </FormGrid>
        <FormGrid columns={4}>
          <Input label="Quote #" value={quote.quote_number || ''} onChange={setField('quote_number')} required disabled={readOnly} />
          <Input label="Quote Date" type="date" value={quote.quote_date || ''} onChange={setField('quote_date')} disabled={readOnly} />
          <Input label="Valid Days" type="number" min="0" value={quote.quote_valid_days ?? ''} onChange={setNumField('quote_valid_days')} disabled={readOnly} />
          <Input label="Quoted By" value={quote.quoted_by || ''} onChange={setField('quoted_by')} disabled={readOnly} />
        </FormGrid>
        <FormGrid columns={2}>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Customer</label>
            <CustomerPicker value={quote.customer_id} onChange={handleCustomerChange} isDisabled={readOnly} />
          </div>
          <Input label="Contact" value={quote.contact || ''} onChange={setField('contact')} disabled={readOnly} />
        </FormGrid>
        <Input label="Description" value={quote.description || ''} onChange={setField('description')} disabled={readOnly} />
      </div>

      {/* Customer + Ship-to preview */}
      <FormGrid columns={2}>
        <div className="bg-white border border-slate-200 rounded-sm p-3">
          <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Bill To</h3>
          {billTo ? (
            <div className="text-sm text-slate-700 space-y-0.5">
              <div className="font-medium">{billTo.name}</div>
              {billTo.contact && <div>{billTo.contact}</div>}
              {billTo.address && <div>{billTo.address}</div>}
              {(billTo.city || billTo.state || billTo.zip) && (
                <div>{[billTo.city, billTo.state].filter(Boolean).join(', ')} {billTo.zip || ''}</div>
              )}
              {billTo.phone && <div className="text-slate-500">{billTo.phone}</div>}
              {billTo.email && <div className="text-slate-500">{billTo.email}</div>}
            </div>
          ) : <div className="text-sm text-slate-400">No customer selected.</div>}
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-3">
          <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Ship To</h3>
          {shipTo ? (
            <div className="text-sm text-slate-700 space-y-0.5">
              <div className="font-medium">{shipTo.name}</div>
              {shipTo.contact && <div>{shipTo.contact}</div>}
              {shipTo.address && <div>{shipTo.address}</div>}
              {(shipTo.city || shipTo.state || shipTo.zip) && (
                <div>{[shipTo.city, shipTo.state].filter(Boolean).join(', ')} {shipTo.zip || ''}</div>
              )}
              {shipTo.phone && <div className="text-slate-500">{shipTo.phone}</div>}
              {shipTo.email && <div className="text-slate-500">{shipTo.email}</div>}
            </div>
          ) : <div className="text-sm text-slate-400">No customer selected.</div>}
        </div>
      </FormGrid>

      {/* Line items */}
      <FreeformQuoteLineEditor
        lines={lines}
        disabled={readOnly}
        onChange={updateLine}
        onAdd={addLine}
        onRemove={removeLine}
      />

      {/* Totals + Notes */}
      <FormGrid columns={2}>
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Total</h3>
          <div className="flex justify-between text-lg">
            <span className="font-semibold text-slate-800">Subtotal</span>
            <span className="font-semibold text-slate-900 tabular-nums">{fmtMoney(totals.subtotal)}</span>
          </div>
          <p className="mt-2 text-xs text-slate-500">Tax &amp; shipping appear on the final invoice, not the quote.</p>
        </div>
        <div className="space-y-3">
          <Textarea label="Customer Notes (prints on quote)" value={quote.customer_notes || ''} onChange={setField('customer_notes')} rows={3} disabled={readOnly} />
          <Textarea label="Internal Notes (not printed)" value={quote.internal_notes || ''} onChange={setField('internal_notes')} rows={3} disabled={readOnly} />
        </div>
      </FormGrid>

      {/* Modals */}
      <Modal isOpen={discardConfirmOpen} onClose={() => setDiscardConfirmOpen(false)} title="Discard changes?" size="md">
        <p className="text-sm text-slate-700">Your unsaved changes will be lost.</p>
        <ModalFooter>
          <ModalButton variant="secondary" onClick={() => setDiscardConfirmOpen(false)}>Keep editing</ModalButton>
          <ModalButton variant="danger" onClick={confirmDiscard}>Discard</ModalButton>
        </ModalFooter>
      </Modal>

      <Modal isOpen={deleteOpen} onClose={() => setDeleteOpen(false)} title="Delete quote?" size="md">
        <p className="text-sm text-slate-700">
          This will permanently delete <span className="font-medium">{quote.quote_number}</span>. This cannot be undone.
        </p>
        <ModalFooter>
          <ModalButton variant="secondary" onClick={() => setDeleteOpen(false)}>Cancel</ModalButton>
          <ModalButton variant="danger" onClick={confirmDelete}>Delete quote</ModalButton>
        </ModalFooter>
      </Modal>

      <Modal isOpen={blocker.state === 'blocked'} onClose={blockerStay} title="Unsaved changes" size="md">
        <p className="text-sm text-slate-700">You have unsaved changes on this quote. What would you like to do?</p>
        <ModalFooter>
          <ModalButton variant="secondary" onClick={blockerStay} disabled={saving}>Stay on page</ModalButton>
          <ModalButton variant="danger" onClick={blockerDiscard} disabled={saving}>Discard</ModalButton>
          <ModalButton variant="primary" onClick={blockerSaveAndLeave} loading={saving}>Save &amp; leave</ModalButton>
        </ModalFooter>
      </Modal>
    </div>
  );
}
